import { FC, useCallback } from 'react';
import { Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import styled from 'styled-components';
import { DocumentInput } from '~/components';
import Form from '~/components/WizardForm';
import TextAreaField from '~/components/fields/TextAreaField';
import Section from '~/layouts/Section ';
import { KsaManualScreeningResult } from '~/types/ksa/KsaManualScreeningResult';
import { NamePath } from '~/utils/buildNamePath';
import colors from '~/constants/colors';
import DecisionField from '../../components/DecisionField';

interface Props {
  result: KsaManualScreeningResult;
  baseNamePath: NamePath;
}

const ManualScreeningResultsView: FC<Props> = ({ result, baseNamePath }: Props) => {
  const [t] = useTranslation('ksaReview');

  const n = useCallback(
    (namePath: NamePath) => [baseNamePath].flat().concat([namePath].flat()),
    [baseNamePath]
  );
  return (
    <>
      <TitleContainer>
        <Typography.Title level={5}>{t('Manual screening results').toUpperCase()}</Typography.Title>
        <Typography.Paragraph style={{ color: colors.grey[600] }}>
          {t('by')} {result.agentName} {dayjs(result.date).format('DD.MM.YYYY HH:mm')}
        </Typography.Paragraph>
      </TitleContainer>
      <Section>
        <Typography.Paragraph>
          {t(
            'In case of complex ownership structure please provide comments and/or schemas that might be relevant.'
          )}
        </Typography.Paragraph>
        {result?.resolution ? (
          <Typography.Paragraph>
            <DecisionField
              label={t('Screening resolution:')}
              value={t(`screeningResolution.${result.resolution}`)}
              result={result?.resolution}
            />
          </Typography.Paragraph>
        ) : undefined}
        {result.files ? (
          <Typography.Paragraph>
            <DocumentInput
              name=""
              maxCount={result.files.length}
              value={result.files}
              noRemove
              multiple={false}
              readonly
            />
          </Typography.Paragraph>
        ) : undefined}
        <Typography.Paragraph>
          {result.comment && <TextAreaField>{result.comment}</TextAreaField>}
        </Typography.Paragraph>
      </Section>

      <Form.Item name={n('uid')} hidden />
      <Form.Item name={n('agentName')} hidden />
      <Form.Item name={n('date')} hidden />
      <Form.Item name={n('files')} hidden />
      <Form.Item name={n('resolution')} hidden />
      <Form.Item name={n('comment')} hidden />
    </>
  );
};

const TitleContainer = styled.div`
  display: flex;
  justify-content: space-between;
`;

export default ManualScreeningResultsView;
